type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type AuthUser = {
  uid?: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified?: boolean;
  providerData?: unknown;
  [key: string]: unknown;
};

export type AuthError = {
  code: string;
  message: string;
  [key: string]: unknown;
};

export type AuthState = {
  userId?: string | null;
  user?: AuthUser | null;
  error?: AuthError | null;
  acl?: unknown;
  loading?: boolean;
  profileUpdating?: boolean;
  profileError?: unknown;
  [key: string]: unknown;
};

const initialState: AuthState = {
  userId: null,
};

const authReducer = (
  state: AuthState = initialState,
  action: ReducerAction
): AuthState => {
  switch (action.type) {
    case 'AUTH_SIGN_UP_WITH_FIREBASE_USER':
    case 'AUTH_LOGIN_WITH_FIREBASE_USER':
    case 'AUTH_SIGN_UP_WITH_FIREBASE_PROVIDER':
    case 'AUTH_LOGIN_WITH_FIREBASE_PROVIDER': {
      // Get only the specific properties we need instead of cloning the entire object
      const { user, error } = action.payload || {};

      // Extract only the properties we need from the user object
      const safeUser = user
        ? {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            providerData: user.providerData,
            // Add any other properties you need
          }
        : null;

      // Ensure error is serializable
      const safeError = error
        ? {
            code: error.code || 'unknown',
            message: error.message || String(error),
            // Only include other specific serializable properties if needed
          }
        : null;

      // Merge rather than fully replace: a full replace here would clobber
      // any other top-level auth slice fields (e.g. `acl` set by
      // AUTH_FINISHED_UPDATING_USER_ACL) and - critically - could race with
      // firebase's own onAuthStateChanged listener, which dispatches
      // AUTH_STATE_CHANGED with the real/correct user moments before this
      // action; a full replace here would silently clobber that correct
      // user with whatever this action's own payload carries.
      //
      // The `user` sub-object itself also needs a merge, not a replace:
      // signUpUser/loginUser's dispatched payload only ever carries
      // `{uid, email, displayName}` (never photoURL/emailVerified/
      // providerData), so `safeUser` above always explicitly has ALL of
      // those keys - just `undefined` when absent from the incoming user.
      // A plain `{ ...state.user, ...safeUser }` spread would still copy
      // those `undefined`-valued keys over (a spread copies own keys
      // regardless of value), resetting fields a prior AUTH_STATE_CHANGED
      // had already populated even though nothing about them actually
      // changed. Only overwrite fields the incoming user actually
      // provided - matching the AUTH_STATE_CHANGED case's merge intent
      // below, whose own (always fully-populated) user object never hits
      // this edge case.
      return {
        ...state,
        user: safeUser
          ? {
              ...state.user,
              ...Object.fromEntries(
                Object.entries(safeUser).filter(([, value]) => {
                  return value !== undefined;
                })
              ),
            }
          : safeUser,
        error: safeError,
      };
    }
    case 'AUTH_RESET_PASSWORD': {
      // A password-reset-email confirmation (or its `{error}` shape) has
      // nothing to do with the current session's `user`/`acl` and must never
      // touch them - not even via a merge. resetPasswordEmail's thunk
      // (auth.actions.js) dispatches this with the RAW resolved value from
      // firebaseTools.resetPasswordEmail as `action.payload`, and that
      // dispatch isn't tied to the ForgotPasswordPage component's lifecycle
      // (nothing cancels it if the route changes). If this same browser tab
      // becomes authenticated (e.g. another tab of the same origin logs in
      // and firebase's LOCAL-persistence onAuthStateChanged broadcasts
      // across tabs) while that request is still in flight, a full-replace
      // here would wipe the just-established `user`/`acl` down to whatever
      // this unrelated action's payload happens to carry, even though
      // firebase itself still holds a valid session.
      //
      // ForgotPasswordPage.react.js never reads this from Redux at all - it
      // only consumes the resetPasswordEmail thunk's own returned promise
      // value (`data.payload`) to render its success/error message. Nothing
      // else in the app reads a reset-password field off `state.auth`
      // either, so there's nothing useful to store here - just leave the
      // auth slice untouched.
      return state;
    }
    case 'AUTH_UPDATE_USER_PROFILE': {
      return {
        ...state,
        user: {
          ...state.user,
          ...action.updates,
        },
      };
    }
    // These three are dispatched by firebase_updateUserProfile
    // (redux-actions/auth.actions.js) around the debounced firebase sync
    // that follows the optimistic AUTH_UPDATE_USER_PROFILE update above.
    // Minimal state only, matching the AUTH_LOGOUT_START/FAILURE loading/
    // error pattern - ProfilePage.react.js doesn't currently read any
    // profile-save loading/error state, so there's no UI to wire this into
    // yet, but the state should exist and be correct once something does.
    case 'AUTH_IS_UPDATING_USER_PROFILE':
    case 'AUTH_FINISHED_UPDATING_USER_PROFILE':
    case 'AUTH_FINISHED_UPDATING_USER_PROFILE_WITH_ERROR': {
      // `.cancel()` on the debounced profile-save (see logoutUser() in
      // auth.actions.js) can only stop the write if its timer hasn't fired
      // yet - once fired, the actual Firebase write is in-flight and these
      // three actions WILL eventually be dispatched regardless, however
      // long that write takes. If a logout (or a different user logging in
      // - AUTH_LOGOUT_SUCCESS/a cross-tab AUTH_STATE_CHANGED for another
      // uid) has already reset/replaced `state.user` by the time this
      // late-arriving dispatch lands, it belongs to a session that is no
      // longer current - applying it would pollute the (different, or
      // logged-out/initial) current state with stray
      // profileUpdating/profileError fields for a write nobody here asked
      // about. Only apply it while there's still a genuine current user.
      if (!state.user) {
        return state;
      }
      if (action.type === 'AUTH_IS_UPDATING_USER_PROFILE') {
        return {
          ...state,
          profileUpdating: true,
          profileError: null,
        };
      }
      if (action.type === 'AUTH_FINISHED_UPDATING_USER_PROFILE') {
        return {
          ...state,
          profileUpdating: false,
          profileError: null,
        };
      }
      return {
        ...state,
        profileUpdating: false,
        profileError: action.payload.error,
      };
    }
    case 'AUTH_STATE_CHANGED': {
      const user = action.payload;
      if (!user || Object.keys(user).length === 0) {
        return state;
      }

      // Extract only the properties we need from the user object
      const safeUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData,
        // Add any other properties you need
      };

      // Firebase's default LOCAL auth persistence (no setPersistence call
      // anywhere in this app) broadcasts onAuthStateChanged across every
      // tab of the same browser origin - so this case must distinguish
      // "same user, just refreshed" from "a genuinely different user
      // signed in in another tab" BEFORE deciding how to combine the
      // incoming user with `state.user`. Only compare uids when there
      // actually is a previous user with a uid to compare against -
      // otherwise (first login, or a previous case that never set a uid)
      // there's nothing to distinguish from, so fall through to the
      // same-user merge path below.
      const isDifferentUser =
        state.user?.uid !== undefined &&
        safeUser.uid !== undefined &&
        state.user.uid !== safeUser.uid;

      if (isDifferentUser) {
        // A genuinely different identity (e.g. cross-tab login as a
        // different user) must not inherit ANY field from the previous
        // user's session - not even via the null-filtering merge below,
        // which exists specifically to protect a locally-set displayName
        // against the SAME user's own stale `null`. Extending that
        // protection across a uid change is exactly the bug: the new
        // user's real Auth `displayName` is *also* commonly `null` (every
        // email/password account's Auth profile stays null forever - see
        // the comment below), so the old filter would silently preserve
        // the PREVIOUS user's displayName/photoURL onto what is now a
        // different account. Only filter `undefined` here (still needed -
        // see the sign-up/login cases above for why `undefined` can mean
        // "key not sent", not "clear it") - a genuine `null` must land
        // as-is, since for a different user it truly does mean "this
        // account has no display name set".
        return {
          ...state,
          user: Object.fromEntries(
            Object.entries(safeUser).filter(([, value]) => {
              return value !== undefined;
            })
          ),
        };
      }

      // Filter out `null`/`undefined`-valued keys from the incoming
      // Firebase user before merging - a plain spread would let either
      // one clobber a genuinely-set local value. This matters because
      // firebase_updateUserProfile only writes to the Realtime DB (the
      // actual Firebase Auth profile update is commented out), so an
      // email/password-signup user's Firebase Auth `displayName` stays
      // `null` forever - every subsequent AUTH_STATE_CHANGED (e.g. on
      // page refresh) would otherwise stomp a locally-set display name
      // back to `null`. Unlike the sign-up/login cases above (which only
      // filter `undefined`, since their payload never carries
      // photoURL/emailVerified/providerData at all), this case's
      // `safeUser` is always fully populated from the real Auth user, so
      // `null` here specifically means "Firebase doesn't have this set" -
      // not "this field is being cleared" - and must not override a value
      // this app already knows about from its own DB-stored profile. This
      // is only safe because we've already confirmed above (isDifferentUser)
      // that this is the SAME user as before, e.g. a token refresh.
      return {
        ...state,
        user: {
          ...state.user,
          ...Object.fromEntries(
            Object.entries(safeUser).filter(([, value]) => {
              return value !== undefined && value !== null;
            })
          ),
        },
      };
    }
    case 'AUTH_FINISHED_UPDATING_USER_ACL': {
      // Mirrors the `!state.user` guard on AUTH_IS_UPDATING_USER_PROFILE/
      // AUTH_FINISHED_UPDATING_USER_PROFILE/_WITH_ERROR above. This action is
      // dispatched by the onAuthStateChanged listener's ACL-refresh
      // `callback` (auth.actions.js) after `await user.getIdToken(true)` - a
      // real network round-trip that can still be in flight when a logout
      // (or a different user logging in, cross-tab, then logging out) has
      // already reset `state.user` to null by the time this late-arriving
      // dispatch lands. The action-creator-level uid check in auth.actions.js
      // is the primary defense against a DIFFERENT user's stale acl landing
      // here; this is defense-in-depth against the logged-out case
      // specifically, so a stale acl can never attach itself to `initialState`.
      if (!state.user) {
        return state;
      }
      return {
        ...state,
        acl: action.payload,
      };
    }
    case 'AUTH_LOGOUT_START': {
      return {
        ...state,
        loading: true,
        error: null,
      };
    }
    // 'AUTH_LOGOUT' is confirmed unreachable in production - no action
    // creator ever dispatches a bare `{type: 'AUTH_LOGOUT'}` (only test
    // mocks of logoutUser() do; the real thunk dispatches
    // AUTH_LOGOUT_START/AUTH_LOGOUT_SUCCESS/AUTH_LOGOUT_FAILURE). It used to
    // have its own case doing an unguarded `return action.payload` - the
    // same unguarded-full-replace bug class already fixed for
    // AUTH_RESET_PASSWORD - which would wipe the ENTIRE auth slice down to
    // whatever arbitrary payload the action happened to carry, if this ever
    // got wired up for real. Every other reducer that resets on logout
    // (settings/accounts/ledgers/ledgersList/portfolios/portfoliosList/
    // admin) already handles 'AUTH_LOGOUT' as a fallthrough alongside
    // 'AUTH_LOGOUT_SUCCESS' with its own (payload-independent) reset - mirror
    // that same pattern here instead of a separate, payload-driven case.
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return initialState;
    }
    case 'AUTH_LOGOUT_FAILURE': {
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };
    }
    default:
      return state;
  }
};

export default authReducer;
